import { MaterialCommunityIcons, MaterialIcons } from "@expo/vector-icons";
import { SortOrder } from "@nozbe/watermelondb/QueryDescription";
import {
  Divider,
  Text,
  useThemeProvider,
} from "@towersystems/roam-common-native";
import { useCallback, useMemo, useState } from "react";
import {
  FlatList,
  Pressable,
  TouchableOpacity,
  useWindowDimensions,
} from "react-native";
import { Spacings, View } from "react-native-ui-lib";
import StockCount from "../../../../../../utilities/data/model/StockCount";
import StockCountLine from "../../../../../../utilities/data/model/StockCountLine";
import {
  StockCountSyncContextType,
  StockSelectionType,
} from "../../../../../../utilities/stock-count";
import {
  useCurrentCountContext,
  useStockCountSearchContext,
} from "../../../../../../utilities/stock-count/hooks";
import { ListItemVariants } from "../../../../components";
import { SceneProps } from "../../types";
import { StockCountListItem } from "../StockCountListItem";
import styles from "./styles";
import { FlatListSearchableSkeleton } from "../../../../../../components/FlatListSearchableSkeleton";
import { SMALL_SCREEN_BREAKPOINT } from "../../../../common";

export type StockCountSceneProps = SceneProps &
  StockCountSyncContextType & {
    stockCount: StockCount;
    totalItemsCount: number;
    items: StockCountLine[];
    compact?: boolean;
  };

enum SceneColumns {
  EXPECTED = "expected",
  COUNTED = "counted",
}

export const StockCountScene = ({
  stockCount,
  totalItemsCount,
  items,
  loadingStockCountLines,
}: StockCountSceneProps) => {
  const { colors } = useThemeProvider();
  const layout = useWindowDimensions();

  const isCompactView = layout.width < SMALL_SCREEN_BREAKPOINT;

  const [preferredColumn, setPreferredColumn] = useState<SceneColumns>(
    SceneColumns.COUNTED,
  );

  const {
    stockSelection,
    selectedStockCountLine,
    updateStockSelection,
    updateSelectedStockCountLine,
  } = useCurrentCountContext();

  const selectedStockCountId = stockCount.id;

  const { sortBy } = useStockCountSearchContext();

  const handleOnPress = useCallback(
    async (item: StockCountLine) => {
      updateStockSelection({
        stockIdentifier: {
          productId: item.product.id ?? undefined,
          barcode: item.barcode || undefined,
        },
        triggerUpdatedAt: false,
        selectionType: StockSelectionType.MANUAL_ENTRY,
      });
      updateSelectedStockCountLine(item);
    },
    [
      stockSelection,
      selectedStockCountId,
      updateStockSelection,
      updateSelectedStockCountLine,
    ],
  );

  const variant = useMemo(() => {
    if (!isCompactView) {
      return undefined;
    }
    if (preferredColumn === SceneColumns.COUNTED) {
      return ListItemVariants.COUNTED;
    }
    if (preferredColumn === SceneColumns.EXPECTED) {
      return ListItemVariants.EXPECTED;
    }
    return undefined;
  }, [preferredColumn, isCompactView]);

  const renderStockCount = useCallback(
    ({ item }: { item: StockCountLine }) => {
      return (
        <StockCountListItem
          stockCountLine={item}
          selected={item.id === selectedStockCountLine?.id}
          onPress={() => handleOnPress(item)}
          variant={variant}
        />
      );
    },
    [selectedStockCountLine?.id, selectedStockCountId, variant],
  );

  const togglePreferredColumn = useCallback(
    () =>
      setPreferredColumn((prev) =>
        prev === SceneColumns.COUNTED
          ? SceneColumns.EXPECTED
          : SceneColumns.COUNTED,
      ),
    [],
  );

  const baseHeaderStyles = {
    alignItems: "flex-end",
    padding: Spacings.s4,
    width: 100,
  } as const;

  const largeHeaderStyles = {
    width: 40,
  };

  const commonHeaderTextProps = {
    variant: "heading4",
    fontWeight: "bold",
    textDecorationLine: "underline",
  } as const;

  const headerMarkup = (
    <View
      style={{
        flexDirection: "row",
        borderBottomWidth: 2,
        borderBottomColor: colors.borderColor,
        alignItems: "center",
      }}
    >
      <View style={{ flexGrow: 1, padding: Spacings.s4 }}></View>
      {isCompactView && (
        <TouchableOpacity onPress={togglePreferredColumn}>
          <MaterialIcons name="compare-arrows" size={24} color={colors.text} />
        </TouchableOpacity>
      )}
      {(!isCompactView || preferredColumn === SceneColumns.EXPECTED) && (
        <View style={[baseHeaderStyles, !isCompactView && largeHeaderStyles]}>
          <Pressable
            onPress={() => {
              sortBy.sort("expected");
            }}
            style={[styles.SortButton]}
          >
            <Text {...commonHeaderTextProps}>Expected</Text>
            {renderSortedIcon(sortBy.sortBy?.expected)}
          </Pressable>
        </View>
      )}
      {(!isCompactView || preferredColumn === SceneColumns.COUNTED) && (
        <View style={[baseHeaderStyles]}>
          <Pressable
            onPress={() => {
              sortBy.sort("counted");
            }}
            style={[styles.SortButton]}
          >
            <Text {...commonHeaderTextProps}>Counted</Text>
            {renderSortedIcon(sortBy.sortBy?.counted)}
          </Pressable>
        </View>
      )}
      <View style={[baseHeaderStyles]}>
        <Pressable>
          <Text {...commonHeaderTextProps}>Status</Text>
        </Pressable>
      </View>
    </View>
  );

  const remainingItemsCount = totalItemsCount - items.length;

  const remainingItemsMarkup =
    remainingItemsCount > 0 ? (
      <View style={{ padding: Spacings.s4, alignItems: "center" }}>
        <Text variant="body" color={colors.disabledText}>
          {remainingItemsCount} more items not shown
        </Text>
      </View>
    ) : null;

  return (
    <View>
      {headerMarkup}
      {loadingStockCountLines && items.length <= 0 ? (
        <FlatListSearchableSkeleton searchbar={false} />
      ) : (
        <FlatList
          windowSize={5}
          initialNumToRender={5}
          keyExtractor={(item) => item.id}
          ItemSeparatorComponent={() => <Divider marginVertical={0} />}
          data={items.slice(0, 22)}
          renderItem={renderStockCount}
        />
      )}
      {remainingItemsMarkup}
    </View>
  );
};

const renderSortedIcon = (sortOrder?: SortOrder) => {
  if (sortOrder == "asc") {
    return (
      <MaterialCommunityIcons name="sort-ascending" size={20} color="black" />
    );
  }
  if (sortOrder == "desc") {
    return (
      <MaterialCommunityIcons name="sort-descending" size={20} color="black" />
    );
  }
  return undefined;
};
